import { useEffect, useState } from "react";
import { X, ChevronDown, ShieldCheck } from "lucide-react";
import { TRIP_SECURE_BENEFITS } from "./tripSecureBenefits";

/**
 * "Benefits of Trip Secure + Insurance" — the dialog behind the info icon on the
 * auto-add insurance section. One accordion section per product, each listing
 * what that cover includes.
 */
export default function TripSecureBenefitsDialog({ isOpen, onClose }) {
  // Only one section open at a time; the first starts expanded so the dialog
  // never opens as four collapsed rows with nothing to read.
  const [openKey, setOpenKey] = useState(TRIP_SECURE_BENEFITS[0].key);

  // Escape closes, and the page behind must not scroll while the dialog is up.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/50 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="trip-secure-title"
        // Stops a click inside the panel from reaching the backdrop's onClose.
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg my-8 max-h-[85vh] flex flex-col"
      >
        <div className="flex items-start justify-between gap-4 p-6 pb-4 shrink-0">
          <div>
            <h2 id="trip-secure-title" className="text-xl font-bold text-gray-900 tracking-[-0.02em]">
              Benefits of Trip Secure + Insurance
            </h2>
            <p className="text-sm text-teal-700 font-medium mt-0.5">
              Secure your trip against sudden events
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center hover:bg-gray-200 transition-colors shrink-0 cursor-pointer"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-6 shrink-0">
          <p className="rounded-lg bg-amber-50 border border-amber-100 px-4 py-2.5 text-xs text-gray-600">
            Coverage and benefits may differ depending on the selected plan.
          </p>
        </div>

        <div className="overflow-y-auto px-6 py-4 flex-1">
          {TRIP_SECURE_BENEFITS.map((group) => {
            const isOpenSection = openKey === group.key;
            return (
              <div key={group.key} className="border-b border-gray-100 last:border-0">
                <button
                  type="button"
                  onClick={() => setOpenKey(isOpenSection ? null : group.key)}
                  aria-expanded={isOpenSection}
                  className="w-full flex items-center gap-3 py-3.5 text-left cursor-pointer group"
                >
                  <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-gray-700 whitespace-nowrap">
                    {group.label}
                  </span>
                  <span className="flex-1 h-px bg-gray-200" aria-hidden="true" />
                  {/* Count is derived, so it can never disagree with the list. */}
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {group.benefits.length} benefits
                  </span>
                  <span
                    className={`w-6 h-6 rounded-full bg-teal-600 text-white flex items-center justify-center shrink-0 transition-transform ${
                      isOpenSection ? "rotate-180" : ""
                    }`}
                  >
                    <ChevronDown size={14} />
                  </span>
                </button>

                {isOpenSection && (
                  <ul className="pb-4 space-y-4">
                    {group.benefits.map((benefit) => (
                      <li key={benefit.title} className="flex gap-3">
                        <ShieldCheck size={16} className="text-teal-600 shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900">{benefit.title}</p>
                          <p className="text-xs text-gray-500 leading-relaxed mt-0.5">
                            {benefit.description}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
